import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence, validateFormat } from 'ember-changeset-validations/validators';
import type SummaryPageModel from 'garaje/models/summary-page';
import type GlobalSettingBatchService from 'garaje/services/global-setting-batch';
import type { ModelData } from 'garaje/utils/type-utils';
import validateConditional from 'garaje/validators/conditional';
import { alias } from 'macro-decorators';

interface FlowsSectionsFinalScreenComponentSignature {
  Args: {
    summaryPage: SummaryPageModel;
    changeset?: DetailedChangeset<SummaryPageModel>;
  };
}

function buildValidations() {
  return {
    videoUrl: validateConditional({
      if(_key, _newValue, _oldValue, changes, model) {
        return ((<ModelData<SummaryPageModel>>changes).kind || (<SummaryPageModel>model).kind) === 'video';
      },
      then: validateFormat({
        type: 'url',
        allowBlank: false,
      }),
      else() {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
    message: validateConditional({
      if(_key, _newValue, _oldValue, changes, model) {
        return ((<ModelData<SummaryPageModel>>changes).kind || (<SummaryPageModel>model).kind) === 'message';
      },
      then: validatePresence(true),
      else() {
        // Mark this field as valid, We don't need to validate since we
        // don't use the location value
        return true;
      },
    }),
  };
}

export default class FlowsSectionsFinalScreenComponent extends Component<FlowsSectionsFinalScreenComponentSignature> {
  @service declare globalSettingBatch: GlobalSettingBatchService;

  @alias('globalSettingBatch.currentGlobalSettingBatch')
  currentGlobalSettingBatch!: GlobalSettingBatchService['currentGlobalSettingBatch'];

  @tracked summaryPage;
  @tracked changeset;

  constructor(owner: unknown, args: FlowsSectionsFinalScreenComponentSignature['Args']) {
    super(owner, args);

    this.summaryPage = this.args.summaryPage;
    this.changeset = this.args.changeset ?? this.#buildChangeset();
  }

  #buildChangeset() {
    const validations = buildValidations();
    const validator = lookupValidator(validations);
    return Changeset(this.summaryPage, validator, validations);
  }
}
